import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  LogOut, 
  Calendar, 
  Car, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  Euro,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  Star,
  Lock,
  Settings,
  Users,
  Trash2,
  Archive,
  Gift,
  FileText,
  Menu,
  X,
  CreditCard,
  Copy,
  ExternalLink,
  Loader2
} from 'lucide-react';
import adminAxios from '../utils/axiosConfig';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AdminQuotes from './AdminQuotes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const mainTab = searchParams.get('tab') || 'bookings';
  
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Payment Link State
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaymentLinkDialog, setShowPaymentLinkDialog] = useState(false);
  const [paymentLink, setPaymentLink] = useState('');
  const [loadingPayment, setLoadingPayment] = useState(false);
  
  // Alternative Vehicle Proposal State
  const [showAlternativeDialog, setShowAlternativeDialog] = useState(false);
  const [proposedVehicle, setProposedVehicle] = useState('');
  const [adminNote, setAdminNote] = useState('');
  
  // Create Work Order State
  const [showCreateWorkOrder, setShowCreateWorkOrder] = useState(false);
  const [workOrderData, setWorkOrderData] = useState({
    pickup: '',
    dropoff: '',
    date: '',
    time: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    distance: 0,
    duration: 0,
    calculatedPrice: 0,
    finalPrice: 0,
    assignedDriverId: '',
    sendClientConfirmation: false,
    discountType: 'none',
    discountValue: 0,
    manualPrice: 0
  });
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  
  const availableVehicles = [
    'Lexus ES300h',
    'Mercedes E-Class',
    'BMW Série 5',
    'Mercedes E-Class Break'
  ];

  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/admin/login');
      return;
    }
    
    fetchData();
    fetchDrivers();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        adminAxios.get(`/bookings`),
        adminAxios.get(`/admin/stats`)
      ]);
      
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await adminAxios.get(`/drivers?status=active`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des chauffeurs:', error);
    }
  };

  // Calculate distance for work order
  const calculateWorkOrderDistance = async () => {
    if (!workOrderData.pickup || !workOrderData.dropoff) {
      toast.error('Veuillez renseigner les adresses de départ et d\'arrivée');
      return;
    }

    setCalculatingDistance(true);
    try {
      const response = await adminAxios.post(`/calculate-distance`, {
        origin: workOrderData.pickup,
        destination: workOrderData.dropoff
      });

      const distance = response.data.distance;
      const duration = response.data.duration;
      
      // Calculer le prix (logique simplifiée)
      const basePrice = 8;
      const pricePerKm = 2;
      const pricePerMinute = 0.5;
      let calculatedPrice = basePrice + (distance * pricePerKm) + (duration * pricePerMinute);
      
      // Arrondir
      calculatedPrice = Math.round(calculatedPrice * 100) / 100;

      setWorkOrderData(prev => ({
        ...prev,
        distance,
        duration,
        calculatedPrice,
        finalPrice: calculatedPrice
      }));

      toast.success(`Distance calculée : ${distance} km`);
    } catch (error) {
      console.error('Error calculating distance:', error);
      toast.error('Erreur lors du calcul de la distance');
    } finally {
      setCalculatingDistance(false);
    }
  };

  // Calculate final price with discount
  const calculateWorkOrderFinalPrice = () => {
    const basePrice = workOrderData.calculatedPrice;
    let finalPrice = basePrice;
    
    if (workOrderData.discountType === 'percentage' && workOrderData.discountValue > 0) {
      finalPrice = basePrice * (1 - workOrderData.discountValue / 100);
    } else if (workOrderData.discountType === 'fixed' && workOrderData.discountValue > 0) {
      finalPrice = basePrice - workOrderData.discountValue;
    } else if (workOrderData.discountType === 'manual' && workOrderData.manualPrice > 0) {
      finalPrice = workOrderData.manualPrice;
    }
    
    return Math.max(0, Math.round(finalPrice * 100) / 100);
  };

  // Create work order
  const handleCreateWorkOrder = async () => {
    // Validation
    if (!workOrderData.pickup || !workOrderData.dropoff) {
      toast.error('Veuillez renseigner les adresses');
      return;
    }
    if (!workOrderData.date || !workOrderData.time) {
      toast.error('Veuillez renseigner la date et l\'heure');
      return;
    }
    if (!workOrderData.customerName || !workOrderData.customerPhone) {
      toast.error('Veuillez renseigner le nom et téléphone du client');
      return;
    }
    if (workOrderData.sendClientConfirmation && !workOrderData.customerEmail) {
      toast.error('Email requis pour envoyer la confirmation au client');
      return;
    }

    try {
      const finalPrice = calculateWorkOrderFinalPrice();
      
      const bookingPayload = {
        service: 'transfer',
        vehicle: 'berline',
        date: workOrderData.date,
        time: workOrderData.time,
        pickup: workOrderData.pickup,
        dropoff: workOrderData.dropoff,
        distance: workOrderData.distance,
        duration: workOrderData.duration,
        customerName: workOrderData.customerName,
        customerEmail: workOrderData.customerEmail || 'noemail@example.com',
        customerPhone: workOrderData.customerPhone,
        estimatedPrice: finalPrice,
        calculatedPrice: workOrderData.calculatedPrice,
        discountType: workOrderData.discountType,
        discountValue: workOrderData.discountValue,
        manualPrice: workOrderData.manualPrice,
        finalPrice: finalPrice,
        status: 'confirmed' // Créé directement confirmé
      };

      // Créer la réservation
      const response = await adminAxios.post(`/bookings/create`, bookingPayload);
      const newBooking = response.data;

      // Assigner chauffeur si sélectionné
      if (workOrderData.assignedDriverId) {
        await adminAxios.post(`/bookings/${newBooking.id}/assign`, {
          driverId: workOrderData.assignedDriverId
        });
      }

      toast.success('✅ Bon de commande créé avec succès !');
      
      // Rafraîchir la liste
      fetchData();
      
      // Fermer le dialog et réinitialiser
      setShowCreateWorkOrder(false);
      setWorkOrderData({
        pickup: '',
        dropoff: '',
        date: '',
        time: '',
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        distance: 0,
        duration: 0,
        calculatedPrice: 0,
        finalPrice: 0,
        assignedDriverId: '',
        sendClientConfirmation: false,
        discountType: 'none',
        discountValue: 0,
        manualPrice: 0
      });
      
    } catch (error) {
      console.error('Error creating work order:', error);
      toast.error('Erreur lors de la création du bon de commande');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
    toast.success('Déconnexion réussie');
  };

  const openConfirmDialog = (booking) => {
    setSelectedBooking(booking);
    setSelectedDriver('');
    setShowDriverDialog(true);
  };

  const handleConfirmWithDriver = async () => {
    if (!selectedDriver) {
      toast.error('Veuillez sélectionner un chauffeur');
      return;
    }

    try {
      await adminAxios.put(`/bookings/${selectedBooking.id}/status`, {
        status: 'confirmed',
        assignedDriverId: selectedDriver
      });
      toast.success('Réservation confirmée et chauffeur assigné !');
      setShowDriverDialog(false);
      setSelectedBooking(null);
      setSelectedDriver('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la confirmation');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await adminAxios.put(`/bookings/${bookingId}/status`, { status });
      toast.success(`Réservation ${status === 'confirmed' ? 'confirmée' : status === 'rejected' ? 'refusée' : 'mise à jour'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resendWorkOrder = async (bookingId, driverName) => {
    try {
      const response = await adminAxios.post(`/bookings/${bookingId}/resend-work-order`);
      toast.success(`Bon de commande renvoyé à ${driverName} !`);
      console.log('Work order resent:', response.data);
    } catch (error) {
      console.error('Error resending work order:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi du bon de commande';
      toast.error(errorMessage);
    }
  };

  const sendReceipt = async (bookingId, customerName) => {
    try {
      const response = await adminAxios.post(`/bookings/${bookingId}/send-receipt`);
      toast.success(`Reçu envoyé à ${customerName} !`);
      console.log('Receipt sent:', response.data);
    } catch (error) {
      console.error('Error sending receipt:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de l\'envoi du reçu';
      toast.error(errorMessage);
    }
  };


  const handleProposeAlternative = (booking) => {
    setSelectedBooking(booking);
    setProposedVehicle('');
    setAdminNote('');
    setShowAlternativeDialog(true);
  };

  const submitAlternativeProposal = async () => {
    if (!proposedVehicle) {
      toast.error('Veuillez sélectionner un véhicule');
      return;
    }

    try {
      await adminAxios.put(
        `/admin/bookings/${selectedBooking.id}/propose-alternative`,
        {
          proposedVehicle,
          adminNote
        }
      );

      await fetchData();
      setShowAlternativeDialog(false);
      toast.success(`Proposition envoyée au client par email !`);
    } catch (error) {
      console.error('Error proposing alternative:', error);
      toast.error('Erreur lors de l\'envoi de la proposition');
    }
  };


  const handleArchive = async (bookingId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver cette réservation ? Elle sera masquée mais conservée dans la base de données.')) {
      return;
    }

    try {
      await adminAxios.put(`/bookings/${bookingId}/archive`);
      toast.success('Réservation archivée avec succès');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('⚠️ Êtes-vous sûr de vouloir ANNULER cette réservation ?\n\nUn email d\'annulation sera automatiquement envoyé au client.')) {
      return;
    }
    
    try {
      const response = await adminAxios.put(`/bookings/${bookingId}/cancel`);
      toast.success('Réservation annulée et email envoyé au client');
      fetchData();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const handleDelete = async (bookingId) => {
    if (!window.confirm('⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer DÉFINITIVEMENT cette réservation ? Cette action est IRRÉVERSIBLE.')) {
      return;
    }

    // Double confirmation for delete
    if (!window.confirm('Confirmation finale : Cette réservation sera DÉFINITIVEMENT supprimée. Continuer ?')) {
      return;
    }

    try {
      await adminAxios.delete(`/bookings/${bookingId}`);
      toast.success('Réservation supprimée définitivement');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };


  // Create Stripe Payment Link
  const createPaymentLink = async (booking) => {
    setSelectedBooking(booking);
    setLoadingPayment(true);
    setShowPaymentDialog(true);
    
    try {
      const response = await adminAxios.post('/payments/create-checkout', {
        booking_id: booking.id,
        frontend_origin: window.location.origin
      });
      
      setPaymentLink(response.data.url);
      toast.success('Lien de paiement créé avec succès !');
    } catch (error) {
      console.error('Payment link error:', error);
      toast.error('Erreur lors de la création du lien de paiement');
      setShowPaymentDialog(false);
    } finally {
      setLoadingPayment(false);
    }
  };

  const copyPaymentLink = () => {
    navigator.clipboard.writeText(paymentLink);
    toast.success('Lien copié dans le presse-papier !');
  };


  const getStatusBadge = (status) => {
    const configs = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50', icon: AlertCircle, label: 'En Attente' },
      confirmed: { color: 'bg-green-500/20 text-green-400 border-green-500/50', icon: CheckCircle, label: 'Confirmé' },
      rejected: { color: 'bg-red-500/20 text-red-400 border-red-500/50', icon: XCircle, label: 'Refusé' },
      completed: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/50', icon: CheckCircle, label: 'Terminé' },
      archived: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/50', icon: Archive, label: 'Archivé' },
      alternative_proposed: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/50', icon: Car, label: 'Alternative Proposée' }
    };
    
    const config = configs[status] || configs.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const handleAcceptAndSendPayment = async (booking) => {
    if (!window.confirm(`Confirmer la réservation de ${booking.customerName} et envoyer le lien de paiement ?`)) {
      return;
    }

    try {
      setActionLoading(booking.id);
      
      const response = await adminAxios.post(
        `/admin/bookings/${booking.id}/accept-and-send-payment`
      );
      
      if (response.data.success) {
        toast.success(`✅ Réservation confirmée et lien de paiement envoyé à ${booking.customerEmail}`);
        
        // Show payment link dialog
        setPaymentLink(response.data.payment_url);
        setShowPaymentLinkDialog(true);
        
        // Refresh bookings
        await fetchData();
      }
    } catch (error) {
      console.error('Error accepting booking and sending payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setActionLoading(null);
    }
  };


  // Filter bookings based on active tab
  const filteredBookings = bookings.filter(booking => {
    if (activeTab === 'all') return booking.status !== 'archived';
    if (activeTab === 'archived') return booking.status === 'archived';
    return booking.status === activeTab;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-900 to-black border-b border-gold/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Star className="w-8 h-8 text-gold" />
              <h1 className="text-xl sm:text-2xl font-bold text-white">Admin Dashboard</h1>
            </div>
            <div className="hidden lg:flex items-center gap-3">
              <Button 
                onClick={() => setSearchParams({ tab: 'bookings' })}
                variant={mainTab === 'bookings' ? 'default' : 'outline'}
                className={mainTab === 'bookings' ? 'bg-gold text-black' : 'border-gold/30 text-gold hover:bg-gold/10'}
                size="sm"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Réservations
              </Button>
              <Button 
                onClick={() => {
                  setSearchParams({ tab: 'bookings' });
                  setActiveTab('archived');
                }}
                variant="outline"
                className="border-gray-500/30 text-gray-300 hover:bg-gray-500/10"
                size="sm"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archives
              </Button>
              <Button 
                onClick={() => setSearchParams({ tab: 'quotes' })}
                variant={mainTab === 'quotes' ? 'default' : 'outline'}
                className={mainTab === 'quotes' ? 'bg-gold text-black' : 'border-gold/30 text-gold hover:bg-gold/10'}
                size="sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Devis
              </Button>
              <div className="h-6 w-px bg-gold/30" />
              <Button 
                onClick={() => navigate('/admin/drivers')}
                variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10"
                size="sm"
              >
                <Users className="w-4 h-4 mr-2" />
                Chauffeurs
              </Button>
              <Button 
                onClick={() => navigate('/admin/customers')}
                variant="outline"
                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                size="sm"
              >
                <Users className="w-4 h-4 mr-2" />
                Clients
              </Button>
              <Button 
                onClick={() => navigate('/admin/promotions')}
                variant="outline"
                className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                size="sm"
              >
                <Gift className="w-4 h-4 mr-2" />
                Promos
              </Button>
              <Button 
                onClick={() => navigate('/admin/settings')}
                variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10"
                size="sm"
              >
                <Lock className="w-4 h-4 mr-2" />
                Paramètres
              </Button>
              <Button 
                onClick={() => navigate('/admin/pricing')}
                variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10"
                size="sm"
              >
                <Euro className="w-4 h-4 mr-2" />
                Tarifs
              </Button>
              <Button 
                onClick={() => navigate('/admin/commissions')}
                variant="outline"
                className="border-gold/30 text-gold hover:bg-gold/10"
                size="sm"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Commissions
              </Button>
              <div className="h-6 w-px bg-gold/30" />
              <Button 
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                size="sm"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
            
            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 p-2"
              >
                <LogOut className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="outline"
                size="sm"
                className="border-gold/30 text-gold hover:bg-gold/10 p-2"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
          
          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-4 pb-4 space-y-2 border-t border-gold/20 pt-4">
              <Button 
                onClick={() => { setSearchParams({ tab: 'bookings' }); setMobileMenuOpen(false); }}
                variant={mainTab === 'bookings' ? 'default' : 'outline'}
                className={`w-full justify-start ${mainTab === 'bookings' ? 'bg-gold text-black' : 'border-gold/30 text-gold hover:bg-gold/10'}`}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Réservations
              </Button>
              <Button 
                onClick={() => { setSearchParams({ tab: 'bookings' }); setActiveTab('archived'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-gray-500/30 text-gray-300 hover:bg-gray-500/10"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archives
              </Button>
              <Button 
                onClick={() => { setSearchParams({ tab: 'quotes' }); setMobileMenuOpen(false); }}
                variant={mainTab === 'quotes' ? 'default' : 'outline'}
                className={`w-full justify-start ${mainTab === 'quotes' ? 'bg-gold text-black' : 'border-gold/30 text-gold hover:bg-gold/10'}`}
              >
                <FileText className="w-4 h-4 mr-2" />
                Devis
              </Button>
              <div className="border-t border-gold/20 my-2" />
              <Button 
                onClick={() => { navigate('/admin/drivers'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-gold/30 text-gold hover:bg-gold/10"
              >
                <Users className="w-4 h-4 mr-2" />
                Chauffeurs
              </Button>
              <Button 
                onClick={() => { navigate('/admin/customers'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              >
                <Users className="w-4 h-4 mr-2" />
                Clients
              </Button>
              <Button 
                onClick={() => { navigate('/admin/promotions'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
              >
                <Gift className="w-4 h-4 mr-2" />
                Promos
              </Button>
              <Button 
                onClick={() => { navigate('/admin/settings'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-gold/30 text-gold hover:bg-gold/10"
              >
                <Lock className="w-4 h-4 mr-2" />
                Paramètres
              </Button>
              <Button 
                onClick={() => { navigate('/admin/pricing'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-gold/30 text-gold hover:bg-gold/10"
              >
                <Euro className="w-4 h-4 mr-2" />
                Tarifs
              </Button>
              <Button 
                onClick={() => { navigate('/admin/commissions'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start border-gold/30 text-gold hover:bg-gold/10"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Commissions
              </Button>
              <div className="border-t border-gold/20 my-2" />
              {/* Bouton Créer Bon de Commande Mobile */}
              <Button
                onClick={() => { setShowCreateWorkOrder(true); setMobileMenuOpen(false); }}
                className="w-full bg-gradient-to-r from-gold to-amber-600 hover:from-amber-600 hover:to-gold text-black font-semibold"
              >
                <FileText className="w-4 h-4 mr-2" />
                Créer Bon de Commande
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Conditional Rendering based on mainTab */}
        {mainTab === 'bookings' && (
          <>
            {/* Stats Cards - Clickable */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <Card 
            className="bg-gradient-to-br from-gray-900 to-black border-gold/30 cursor-pointer hover:border-gold/60 transition-all"
            onClick={() => setActiveTab('all')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Total Réservations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats.totalBookings}</div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-yellow-900/20 to-black border-yellow-500/30 cursor-pointer hover:border-yellow-500/60 transition-all"
            onClick={() => setActiveTab('pending')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">En Attente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-400">{stats.pendingBookings}</div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-green-900/20 to-black border-green-500/30 cursor-pointer hover:border-green-500/60 transition-all"
            onClick={() => setActiveTab('confirmed')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Confirmées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{stats.confirmedBookings}</div>
            </CardContent>
          </Card>

          <Card 
            className="bg-gradient-to-br from-blue-900/20 to-black border-blue-500/30 cursor-pointer hover:border-blue-500/60 transition-all"
            onClick={() => setActiveTab('completed')}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400">Terminées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">{stats.completedBookings}</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gold/20 to-black border-gold/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Revenu Total
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gold">{stats.totalRevenue}€</div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings List */}
        <Card className="bg-gradient-to-br from-gray-900 to-black border-gold/30">
          <CardHeader>
            <CardTitle className="text-2xl text-white">Gestion des Réservations</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-800 border-gold/20">
                <TabsTrigger value="all">Toutes</TabsTrigger>
                <TabsTrigger value="pending">En Attente</TabsTrigger>
                <TabsTrigger value="confirmed">Confirmées</TabsTrigger>
                <TabsTrigger value="completed">Terminées</TabsTrigger>
                <TabsTrigger value="archived">
                  <Archive className="w-4 h-4 mr-1" />
                  Archives
                </TabsTrigger>
              </TabsList>

              <TabsContent value={activeTab} className="mt-6 space-y-4">
                {filteredBookings.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    Aucune réservation trouvée
                  </div>
                ) : (
                  filteredBookings.map((booking) => (
                    <Card key={booking.id} className="bg-gray-800/50 border-gold/20">
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-bold text-white">{booking.id}</h3>
                              {getStatusBadge(booking.status)}
                            </div>
                            <p className="text-gray-400 text-sm">
                              Créé le {format(new Date(booking.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-3xl font-bold text-gold">{booking.estimatedPrice}€</div>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Mail className="w-4 h-4 text-gold" />
                              <span className="font-semibold">{booking.customerName}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Mail className="w-4 h-4 text-gold" />
                              {booking.customerEmail}
                            </div>
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Phone className="w-4 h-4 text-gold" />
                              {booking.customerPhone}
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Car className="w-4 h-4 text-gold" />
                              {booking.vehicle}
                            </div>
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Calendar className="w-4 h-4 text-gold" />
                              {booking.date} à {booking.time}
                            </div>
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <MapPin className="w-4 h-4 text-gold" />
                              {booking.pickup}
                            </div>
                            {booking.dropoff && (
                              <div className="flex items-center gap-2 text-gray-400 text-sm pl-6">
                                → {booking.dropoff}
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-sm">
                              <span className={`px-2 py-1 rounded ${booking.paymentMethod === 'cash' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                {booking.paymentMethod === 'cash' ? '💵 Paiement Cash' : '💳 Paiement en ligne'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {booking.status === 'pending' && (
                          <div className="flex flex-col gap-2 pt-4 border-t border-gold/20">
                            {(booking.paymentMethod === 'online' || !booking.paymentMethod) ? (
                              <>
                                <Button
                                  onClick={() => handleAcceptAndSendPayment(booking)}
                                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                                  disabled={actionLoading === booking.id}
                                >
                                  {actionLoading === booking.id ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                  )}
                                  ✅ Accepter et Envoyer Lien de Paiement
                                </Button>
                                <div className="text-xs text-center text-blue-400">
                                  💳 Client souhaite payer en ligne
                                </div>
                              </>
                            ) : booking.paymentMethod === 'cash' ? (
                              <>
                                <Button
                                  onClick={() => openConfirmDialog(booking)}
                                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  ✅ Accepter (Paiement Cash)
                                </Button>
                                <div className="text-xs text-center text-amber-400">
                                  💵 Client paiera en espèces au chauffeur
                                </div>
                              </>
                            ) : null}
                            <div className="flex gap-3 mt-2">
                              <Button
                                onClick={() => updateBookingStatus(booking.id, 'rejected')}
                                variant="outline"
                                className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Refuser
                              </Button>
                              <Button
                                onClick={() => handleProposeAlternative(booking)}
                                variant="outline"
                                className="flex-1 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                              >
                                <Car className="w-4 h-4 mr-2" />
                                Proposer Alternative
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        {/* Archive and Delete buttons for all bookings */}
                        <div className="flex gap-2 pt-3">
                          <Button
                            onClick={() => handleArchive(booking.id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-amber-600/30 text-amber-500 hover:bg-amber-600/10"
                          >
                            <Archive className="w-4 h-4 mr-2" />
                            Archiver
                          </Button>
                          <Button
                            onClick={() => handleDelete(booking.id)}
                            variant="outline"
                            size="sm"
                            className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </Button>
                        </div>

                        {booking.status === 'confirmed' && (
                          <div className="flex flex-col gap-3 pt-4 border-t border-gold/20">
                            {/* Assign Driver Button */}
                            {!booking.assignedDriverId && (
                              <Button
                                onClick={() => openConfirmDialog(booking)}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Users className="w-4 h-4 mr-2" />
                                Assigner un Chauffeur
                              </Button>
                            )}
                            
                            {/* Show assigned driver info */}
                            {booking.assignedDriverId && (
                              <div className="space-y-3">
                                <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-blue-300">
                                    <Users className="w-4 h-4" />
                                    <span className="font-semibold">Chauffeur assigné:</span>
                                    <span className="text-white">{booking.assignedDriverName}</span>
                                  </div>
                                </div>
                                
                                {/* Resend Work Order Button */}
                                <Button
                                  onClick={() => resendWorkOrder(booking.id, booking.assignedDriverName)}
                                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                                  variant="outline"
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  Renvoyer Bon de Commande
                                </Button>
                                
                                {/* Show Payment Link button only for online payments */}
                                {booking.paymentMethod !== 'cash' && (
                                  <Button
                                    onClick={() => createPaymentLink(booking)}
                                    className="w-full bg-gradient-to-r from-gold to-amber-600 hover:from-gold/90 hover:to-amber-600/90 text-black font-semibold"
                                  >
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    Envoyer Lien de Paiement
                                  </Button>
                                )}
                                
                                {/* Show Cash payment indicator */}
                                {booking.paymentMethod === 'cash' && (
                                  <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3">
                                    <div className="flex items-center gap-2 text-green-300">
                                      <Euro className="w-4 h-4" />
                                      <span className="font-semibold">Paiement: Cash</span>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">Le client paiera en espèces</p>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <Button
                              onClick={() => updateBookingStatus(booking.id, 'completed')}
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Marquer Terminé
                            </Button>
                          </div>
                        )}
                        
                        {/* Cancel Button for pending and confirmed bookings */}
                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <div className="pt-4 border-t border-red-500/20">
                            <Button
                              onClick={() => handleCancelBooking(booking.id)}
                              variant="outline"
                              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Annuler la Réservation
                            </Button>
                          </div>
                        )}
                        
                        {booking.status === 'completed' && (
                          <div className="flex flex-col gap-3 pt-4 border-t border-gold/20">
                            <Button
                              onClick={() => sendReceipt(booking.id, booking.customerName)}
                              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Envoyer Reçu au Client
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
          </>
        )}
        
        {/* Quotes Tab */}
        {mainTab === 'quotes' && (
          <AdminQuotes />
        )}
      </div>
      
      {/* Driver Selection Dialog */}
      <Dialog open={showDriverDialog} onOpenChange={setShowDriverDialog}>
        <DialogContent className="bg-gray-900 border-gold/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-gold text-2xl">Confirmer la Réservation</DialogTitle>
            <DialogDescription className="text-gray-400">
              Sélectionnez un chauffeur pour cette course
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="bg-gray-800 p-4 rounded-lg border border-gold/20">
                <p className="text-sm text-gray-400 mb-2">Réservation sélectionnée :</p>
                <p className="text-white font-semibold">{selectedBooking.customerName}</p>
                <p className="text-gray-300 text-sm">{selectedBooking.pickup} → {selectedBooking.dropoff || 'N/A'}</p>
                <p className="text-gray-300 text-sm">{selectedBooking.date} à {selectedBooking.time}</p>
                <p className="text-gold font-semibold mt-2">{selectedBooking.vehicle}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="driver" className="text-white text-base">Chauffeur *</Label>
                {drivers.length === 0 ? (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg">
                    <p className="text-red-400">Aucun chauffeur actif disponible</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Veuillez d&apos;abord ajouter des chauffeurs dans la section &quot;Chauffeurs&quot;
                    </p>
                  </div>
                ) : (
                  <select
                    id="driver"
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-800 border border-gold/30 rounded-md text-white focus:border-gold focus:outline-none"
                    required
                  >
                    <option value="">-- Sélectionnez un chauffeur --</option>
                    {drivers.map((driver) => (
                      <option key={driver.id} value={driver.id}>
                        {driver.firstName} {driver.lastName} - {driver.phone}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowDriverDialog(false)}
                  variant="outline"
                  className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirmWithDriver}
                  disabled={!selectedDriver || drivers.length === 0}
                  className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmer & Assigner
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Alternative Vehicle Proposal Dialog */}
      <Dialog open={showAlternativeDialog} onOpenChange={setShowAlternativeDialog}>
        <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-orange-500 text-2xl">🚗 Proposer un Véhicule Alternatif</DialogTitle>
            <DialogDescription className="text-gray-400">
              Le client recevra un email avec votre proposition et pourra accepter ou refuser
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              {/* Booking Info */}
              <div className="bg-gray-800 p-4 rounded-lg border border-gold/20">
                <p className="text-sm text-gray-400 mb-2">Réservation :</p>
                <p className="text-white font-semibold">{selectedBooking.customerName}</p>
                <p className="text-gray-300 text-sm">{selectedBooking.date} à {selectedBooking.time}</p>
                <p className="text-red-400 font-semibold mt-2">
                  ❌ Véhicule demandé : {selectedBooking.vehicle}
                </p>
              </div>

              {/* Vehicle Selection */}
              <div className="space-y-2">
                <Label htmlFor="proposed-vehicle" className="text-white text-base">
                  Véhicule à proposer *
                </Label>
                <select
                  id="proposed-vehicle"
                  value={proposedVehicle}
                  onChange={(e) => setProposedVehicle(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gold/30 rounded-md text-white focus:border-gold focus:outline-none"
                  required
                >
                  <option value="">-- Sélectionnez un véhicule --</option>
                  {availableVehicles.map((vehicle) => (
                    <option key={vehicle} value={vehicle}>
                      {vehicle}
                    </option>
                  ))}
                </select>
              </div>

              {/* Admin Note */}
              <div className="space-y-2">
                <Label htmlFor="admin-note" className="text-white text-base">
                  Note pour le client (optionnel)
                </Label>
                <textarea
                  id="admin-note"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="Ex: Véhicule de catégorie équivalente, même confort..."
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-800 border border-gold/30 rounded-md text-white focus:border-gold focus:outline-none resize-none"
                />
              </div>

              {/* Info Box */}
              <div className="bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  📧 <strong>Un email sera envoyé au client</strong> avec :
                </p>
                <ul className="text-blue-200 text-sm mt-2 ml-4 space-y-1">
                  <li>• Comparaison véhicule demandé vs proposé</li>
                  <li>• Boutons &quot;Accepter&quot; et &quot;Refuser&quot;</li>
                  <li>• Confirmation immédiate si accepté</li>
                  <li>• Annulation si refusé</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => setShowAlternativeDialog(false)}
                  variant="outline"
                  className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={submitAlternativeProposal}
                  disabled={!proposedVehicle}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 text-white hover:from-orange-700 hover:to-orange-800"
                >
                  <Car className="w-4 h-4 mr-2" />
                  Envoyer la Proposition
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Create Work Order */}
      <Dialog open={showCreateWorkOrder} onOpenChange={setShowCreateWorkOrder}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-900">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gold">Créer un Bon de Commande</DialogTitle>
            <DialogDescription className="text-gray-400">
              Réservation téléphonique - Créer directement un bon de commande
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Adresses */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Adresse de départ *</Label>
                <input
                  type="text"
                  value={workOrderData.pickup}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, pickup: e.target.value }))}
                  placeholder="Bruxelles Centre"
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
              <div>
                <Label className="text-white">Adresse d&apos;arrivée *</Label>
                <input
                  type="text"
                  value={workOrderData.dropoff}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, dropoff: e.target.value }))}
                  placeholder="Louvain"
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
            </div>

            {/* Bouton Calculer Distance */}
            <Button
              onClick={calculateWorkOrderDistance}
              disabled={calculatingDistance || !workOrderData.pickup || !workOrderData.dropoff}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {calculatingDistance ? 'Calcul...' : '🧮 Calculer Distance & Prix'}
            </Button>

            {/* Affichage Distance et Prix */}
            {workOrderData.distance > 0 && (
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gold/20">
                <p className="text-white text-sm">
                  Distance : <strong>{workOrderData.distance} km</strong> | Durée : <strong>{workOrderData.duration} min</strong>
                </p>
                <p className="text-gold text-lg font-bold mt-1">
                  Prix calculé : {workOrderData.calculatedPrice}€
                </p>
              </div>
            )}

            {/* Date & Heure */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Date *</Label>
                <input
                  type="date"
                  value={workOrderData.date}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
              <div>
                <Label className="text-white">Heure *</Label>
                <input
                  type="time"
                  value={workOrderData.time}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
            </div>

            {/* Info Client */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Nom du client *</Label>
                <input
                  type="text"
                  value={workOrderData.customerName}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, customerName: e.target.value }))}
                  placeholder="Jean Dupont"
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
              <div>
                <Label className="text-white">Téléphone *</Label>
                <input
                  type="tel"
                  value={workOrderData.customerPhone}
                  onChange={(e) => setWorkOrderData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="+32 123 456 789"
                  className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">Email (optionnel)</Label>
              <input
                type="email"
                value={workOrderData.customerEmail}
                onChange={(e) => setWorkOrderData(prev => ({ ...prev, customerEmail: e.target.value }))}
                placeholder="jean@example.com"
                className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
              />
            </div>

            {/* Modification Prix */}
            {workOrderData.calculatedPrice > 0 && (
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gold/20">
                <Label className="text-white font-semibold mb-2 block">Ajuster le prix (optionnel)</Label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <Button
                    type="button"
                    variant={workOrderData.discountType === 'percentage' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWorkOrderData(prev => ({ ...prev, discountType: 'percentage', discountValue: 0, manualPrice: 0, finalPrice: prev.calculatedPrice }))}
                    className={workOrderData.discountType === 'percentage' ? 'bg-gold text-black' : 'border-gold/30 text-gold'}
                  >
                    %
                  </Button>
                  <Button
                    type="button"
                    variant={workOrderData.discountType === 'fixed' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWorkOrderData(prev => ({ ...prev, discountType: 'fixed', discountValue: 0, manualPrice: 0, finalPrice: prev.calculatedPrice }))}
                    className={workOrderData.discountType === 'fixed' ? 'bg-gold text-black' : 'border-gold/30 text-gold'}
                  >
                    €
                  </Button>
                  <Button
                    type="button"
                    variant={workOrderData.discountType === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWorkOrderData(prev => ({ ...prev, discountType: 'manual', discountValue: 0, manualPrice: prev.calculatedPrice, finalPrice: prev.calculatedPrice }))}
                    className={workOrderData.discountType === 'manual' ? 'bg-gold text-black' : 'border-gold/30 text-gold'}
                  >
                    Manuel
                  </Button>
                  <Button
                    type="button"
                    variant={workOrderData.discountType === 'none' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setWorkOrderData(prev => ({ ...prev, discountType: 'none', discountValue: 0, manualPrice: 0, finalPrice: prev.calculatedPrice }))}
                    className={workOrderData.discountType === 'none' ? 'bg-gold text-black' : 'border-gold/30 text-gold'}
                  >
                    Aucun
                  </Button>
                </div>

                {workOrderData.discountType === 'percentage' && (
                  <div>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={workOrderData.discountValue}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setWorkOrderData(prev => ({ ...prev, discountValue: value, finalPrice: calculateWorkOrderFinalPrice() }));
                      }}
                      placeholder="10"
                      className="w-full px-3 py-2 bg-gray-700 border border-gold/30 rounded-md text-white"
                    />
                    <p className="text-xs text-green-400 mt-1">
                      Final : {calculateWorkOrderFinalPrice()}€
                    </p>
                  </div>
                )}

                {workOrderData.discountType === 'fixed' && (
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={workOrderData.discountValue}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setWorkOrderData(prev => ({ ...prev, discountValue: value, finalPrice: calculateWorkOrderFinalPrice() }));
                      }}
                      placeholder="20"
                      className="w-full px-3 py-2 bg-gray-700 border border-gold/30 rounded-md text-white"
                    />
                    <p className="text-xs text-green-400 mt-1">
                      Final : {calculateWorkOrderFinalPrice()}€
                    </p>
                  </div>
                )}

                {workOrderData.discountType === 'manual' && (
                  <div>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={workOrderData.manualPrice}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value) || 0;
                        setWorkOrderData(prev => ({ ...prev, manualPrice: value, finalPrice: value }));
                      }}
                      placeholder={workOrderData.calculatedPrice.toString()}
                      className="w-full px-3 py-2 bg-gray-700 border border-gold/30 rounded-md text-white"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Calculé : {workOrderData.calculatedPrice}€ → Final : <span className="text-green-400">{workOrderData.manualPrice}€</span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Assigner Chauffeur */}
            <div>
              <Label className="text-white">Assigner un chauffeur (optionnel)</Label>
              <select
                value={workOrderData.assignedDriverId}
                onChange={(e) => setWorkOrderData(prev => ({ ...prev, assignedDriverId: e.target.value }))}
                className="w-full mt-1 px-3 py-2 bg-gray-800 border border-gold/30 rounded-md text-white"
              >
                <option value="">-- Sélectionner un chauffeur --</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>{driver.name}</option>
                ))}
              </select>
            </div>

            {/* Checkbox Envoyer au Client */}
            <div className="flex items-center space-x-2 p-3 bg-gray-800/50 rounded-lg">
              <input
                type="checkbox"
                id="sendClientConfirmation"
                checked={workOrderData.sendClientConfirmation}
                onChange={(e) => setWorkOrderData(prev => ({ ...prev, sendClientConfirmation: e.target.checked }))}
                className="w-4 h-4 text-gold bg-gray-700 border-gold/30 rounded focus:ring-gold"
              />
              <Label htmlFor="sendClientConfirmation" className="text-white cursor-pointer">
                Envoyer la confirmation au client par email
              </Label>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => {
                  setShowCreateWorkOrder(false);
                  setWorkOrderData({
                    pickup: '',
                    dropoff: '',
                    date: '',
                    time: '',
                    customerName: '',
                    customerPhone: '',
                    customerEmail: '',
                    distance: 0,
                    duration: 0,
                    calculatedPrice: 0,
                    finalPrice: 0,
                    assignedDriverId: '',
                    sendClientConfirmation: false,
                    discountType: 'none',
                    discountValue: 0,
                    manualPrice: 0
                  });
                }}
                variant="outline"
                className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateWorkOrder}
                disabled={!workOrderData.pickup || !workOrderData.dropoff || !workOrderData.date || !workOrderData.time || !workOrderData.customerName || !workOrderData.customerPhone}
                className="flex-1 bg-gradient-to-r from-gold to-amber-600 hover:from-amber-600 hover:to-gold text-black font-semibold"
              >
                <FileText className="w-4 h-4 mr-2" />
                Créer le Bon de Commande
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-gray-900 border-gold/30 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gold flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Lien de Paiement Stripe
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Partagez ce lien sécurisé avec votre client pour qu&apos;il puisse payer en ligne
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {loadingPayment ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold"></div>
                <p className="text-gray-400 mt-4">Création du lien de paiement sécurisé...</p>
              </div>
            ) : (
              <>
                {/* Booking Info */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gold/20">
                  <h3 className="font-semibold text-gold mb-2">Détails de la Réservation</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-400">Client :</span>
                      <span className="ml-2 text-white">{selectedBooking?.customerName}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Montant :</span>
                      <span className="ml-2 text-white font-semibold">
                        {selectedBooking?.finalPrice || selectedBooking?.estimatedPrice}€
                      </span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-gray-400">Trajet :</span>
                      <span className="ml-2 text-white">
                        {selectedBooking?.pickup} → {selectedBooking?.dropoff}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payment Link */}
                <div className="space-y-3">
                  <Label className="text-white font-semibold">Lien de Paiement Sécurisé</Label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={paymentLink}
                      readOnly
                      className="flex-1 bg-gray-800 border border-gold/30 rounded-lg px-4 py-3 text-white text-sm"
                    />
                    <Button
                      onClick={copyPaymentLink}
                      className="bg-gold hover:bg-gold/90 text-black"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400">
                    💡 Ce lien expire dans 24 heures. Le client sera redirigé vers la page de paiement Stripe sécurisée.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    onClick={() => {
                      window.open(paymentLink, '_blank');
                    }}
                    className="flex-1 bg-gradient-to-r from-gold to-amber-600 hover:from-gold/90 hover:to-amber-600/90 text-black font-semibold"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ouvrir le Lien de Paiement
                  </Button>
                  <Button
                    onClick={() => {
                      copyPaymentLink();
                      setShowPaymentDialog(false);
                    }}
                    variant="outline"
                    className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copier et Fermer
                  </Button>
                </div>

                {/* Info Box */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Comment ça marche ?
                  </h4>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>✅ Copiez et envoyez ce lien au client par email/SMS</li>
                    <li>✅ Le client clique et paie en toute sécurité sur Stripe</li>
                    <li>✅ La réservation est automatiquement confirmée après paiement</li>
                    <li>✅ Vous recevez une notification dès que le paiement est validé</li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AdminDashboard;
